'use client';

import type { ReactNode } from 'react';
import Image from 'next/image';
import { cn } from '@/shared/lib/cn';

const THUMB_PX = 22;
// Circles overlap each other by 10% of their width.
const OVERLAP_PX = Math.round(THUMB_PX * 0.1);

interface ThumbStripProps {
  images: string[];
  shape: 'circle' | 'square';
  /** Optional leading label, e.g. "Auto". */
  prefix?: string;
  /** Max thumbnails before collapsing the rest into a "+N" badge. */
  max?: number;
  /** Invert line-art thumbnails (library style icons) for dark/light parity. */
  invert?: boolean;
  /** Black backdrop behind each thumbnail. Used for sketch/control line-art,
   *  which is drawn in light strokes on transparency — a dark fill makes it
   *  clearly visible instead of washing out against the light card. */
  darkBackground?: boolean;
}

/**
 * Compact thumbnail strip for render-settings row subtitles. Circles
 * (project references) overlap by 10%; squares (library style icons) sit in a
 * gap row and are shown as-is. Overflow past `max` collapses into a "+N" chip.
 */
export function ThumbStrip({
  images,
  shape,
  prefix,
  max = 5,
  invert = false,
  darkBackground = false,
}: ThumbStripProps) {
  if (images.length === 0) return null;
  const shown = images.slice(0, max);
  const overflow = images.length - shown.length;
  const isCircle = shape === 'circle';

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {prefix && (
        <span className="text-list-item-subtitle text-muted-foreground whitespace-nowrap">
          {prefix}
        </span>
      )}
      <div className={cn('flex items-center', !isCircle && 'gap-1')}>
        {shown.map((url, i) => (
          <div
            key={`${url}-${i}`}
            style={{
              width: THUMB_PX,
              height: THUMB_PX,
              marginInlineStart: isCircle && i > 0 ? -OVERLAP_PX : 0,
            }}
            className={cn(
              'relative flex-shrink-0 overflow-hidden ring-1 ring-background',
              darkBackground ? 'bg-black' : 'bg-muted',
              isCircle ? 'rounded-full' : 'rounded-[5px]'
            )}
          >
            <Image
              src={url}
              alt=""
              fill
              sizes="22px"
              className={cn('object-cover', invert && 'invert dark:invert-0')}
            />
          </div>
        ))}
        {overflow > 0 && (
          <span
            style={{ marginInlineStart: isCircle ? -OVERLAP_PX : 0 }}
            className="flex-shrink-0 flex items-center justify-center h-[22px] min-w-[22px] px-1 rounded-full bg-muted ring-1 ring-background text-card-meta text-muted-foreground"
          >
            +{overflow}
          </span>
        )}
      </div>
    </div>
  );
}

interface ReferenceSummaryProps {
  /** Image URLs of references the user explicitly selected (manual mode). */
  selectedRefImages: string[];
  /** Image URLs of every project reference in this category (auto = all refs). */
  allRefImages: string[];
  useAutoRefs: boolean;
  isRefine: boolean;
  /** Localized "Auto" prefix shown before the auto-mode circles. */
  autoLabel: string;
}

/**
 * Decides the reference part of a settings-row subtitle:
 * - manual selection → overlapping circles of the picked references
 * - auto + all refs  → "Auto" + circles of every project reference
 * - auto + no refs / empty project → "Auto" label only (always reads as Auto)
 * - No references / Refine → empty slot
 */
export function ReferenceSummary({
  selectedRefImages,
  allRefImages,
  useAutoRefs,
  isRefine,
  autoLabel,
}: ReferenceSummaryProps): ReactNode {
  if (selectedRefImages.length > 0) {
    return <ThumbStrip images={selectedRefImages} shape="circle" />;
  }
  if (isRefine) {
    return null;
  }
  if (useAutoRefs) {
    if (allRefImages.length > 0) {
      return (
        <ThumbStrip images={allRefImages} shape="circle" prefix={autoLabel} />
      );
    }
    // Auto with no project references of this type — still show the "Auto"
    // label (no thumbnails) so the row always reads as Auto.
    return (
      <span className="text-list-item-subtitle text-muted-foreground whitespace-nowrap">
        {autoLabel}
      </span>
    );
  }
  return null;
}
